import { useEffect, useRef, useState } from "react";

// Déclenche l'appel Photon dès 3 caractères, pour tous les appelants du hook.
const MIN_QUERY_LENGTH = 3;
const DEBOUNCE_MS = 300;

// Modes "place" et "postcode" (adresse d'organisme) : l'instance publique de
// Komoot n'a aucun quota chiffré mais "extensive usage will be throttled or
// completely banned". Ces modes économisent donc les requêtes (cache mémoire),
// bornent leur durée et se mettent en pause en cas de limitation/panne.
const REQUEST_TIMEOUT_MS = 8000;
const COOLDOWN_MS = 60000;
const CACHE_MAX_ENTRIES = 100;

export const PHOTON_MODES = {
  // Comportement historique (SearchForm, FilterForm, ProgramStepFormCard) :
  // uniquement `layer=city`. Aucun autre paramètre : l'URL reste identique.
  DESTINATION: "destination",
  // Lieux habités uniquement (ville/commune/village), sans les doublons
  // `boundary=administrative` que `layer=city` laisse passer (Lyon, Marseille).
  PLACE: "place",
  // Codes postaux (`place=postcode`) : ne ressortent sous aucun `layer=city`.
  POSTCODE: "postcode",
};

const PLACE_OSM_TAGS = ["place:city", "place:town", "place:village"];

// Photon 1.x n'accepte que ces langues pour `lang` ; toute autre valeur
// (it, es, en-US, fr-FR…) répond HTTP 400, donc zéro suggestion. À la place
// on omet le paramètre : Photon renvoie alors les noms locaux.
const PHOTON_LANGUAGES = ["fr", "en", "de"];

/** Langue de l'interface ("fr", "en-US"…) vers une langue acceptée par Photon, ou undefined. */
export const getPhotonLang = (language) => {
  const base = String(language || "").toLowerCase().split(/[-_]/)[0];
  return PHOTON_LANGUAGES.includes(base) ? base : undefined;
};
const POSTCODE_LIMIT = "10";
const DEFAULT_LIMIT = "5";

const responseCache = new Map();
let blockedUntil = 0;

/** Réservé aux tests : le cache et la pause sont partagés au niveau du module. */
export const resetPhotonResilienceState = () => {
  responseCache.clear();
  blockedUntil = 0;
};

/**
 * options.mode : voir PHOTON_MODES (défaut DESTINATION).
 * options.countryCode : code ISO — restreint la recherche à ce pays.
 * options.bbox : "minLon,minLat,maxLon,maxLat" — restreint à une emprise.
 * options.lang : langue des noms renvoyés (ville…), voir getPhotonLang. Sans
 * lui, noms locaux de Photon (Köln, Wien) ; avec lui, noms traduits (Cologne,
 * Vienne), le nom local n'étant alors plus fourni.
 * Les modes PLACE et POSTCODE exigent countryCode (sinon aucune requête) : un
 * code postal seul est ambigu entre pays (75001 existe dans 6 pays).
 */
export const buildPhotonUrl = (
  query,
  { mode = PHOTON_MODES.DESTINATION, countryCode, bbox, lang } = {}
) => {
  const url = new URL("https://photon.komoot.io/api/");
  url.searchParams.set("q", query);
  url.searchParams.set(
    "limit",
    mode === PHOTON_MODES.POSTCODE ? POSTCODE_LIMIT : DEFAULT_LIMIT
  );

  if (mode === PHOTON_MODES.POSTCODE) {
    url.searchParams.set("layer", "other");
    url.searchParams.append("osm_tag", "place:postcode");
  } else {
    url.searchParams.set("layer", "city");
    if (mode === PHOTON_MODES.PLACE) {
      PLACE_OSM_TAGS.forEach((tag) => url.searchParams.append("osm_tag", tag));
    }
  }

  if (countryCode) url.searchParams.set("countrycode", countryCode);
  if (bbox) url.searchParams.set("bbox", bbox);
  if (lang) url.searchParams.set("lang", lang);
  return url.toString();
};

const formatFeature = (feature) => {
  const props = feature?.properties || {};
  const coords = feature?.geometry?.coordinates || [];

  if (!coords || coords.length < 2) return null;

  const [lng, lat] = coords;
  // Objet "code postal" : le code est dans `name` (pas de propriété
  // `postcode`), et `name` ne doit surtout pas passer pour un nom de ville.
  const isPostcodeFeature = props.osm_value === "postcode";
  const city =
    props.city ||
    props.locality ||
    props.municipality ||
    (isPostcodeFeature ? "" : props.name) ||
    "";
  const country = props.country || props.countrycode || "";
  const postcode = props.postcode || (isPostcodeFeature ? props.name : "") || "";
  const countryCode = (props.countrycode || "").toUpperCase();

  if ((!city && !isPostcodeFeature) || !Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  return {
    city,
    country,
    countryCode,
    postcode,
    isPostcode: isPostcodeFeature,
    extent: Array.isArray(props.extent) ? props.extent : null,
    lat: Number(lat),
    lng: Number(lng),
  };
};

const rememberResponse = (url, results) => {
  responseCache.set(url, results);
  if (responseCache.size > CACHE_MAX_ENTRIES) {
    responseCache.delete(responseCache.keys().next().value);
  }
};

export const useCityAutocomplete = (query, options = {}) => {
  const { mode = PHOTON_MODES.DESTINATION, countryCode, bbox, lang } = options;
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasRequested, setHasRequested] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const lastQueryRef = useRef("");
  const abortRef = useRef(null);
  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const normalized = query.trim();
    const resilient = mode !== PHOTON_MODES.DESTINATION;
    const canSearch =
      normalized.length >= MIN_QUERY_LENGTH && (!resilient || Boolean(countryCode));

    if (!canSearch) {
      if (abortRef.current) abortRef.current.abort();
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      setResults([]);
      setLoading(false);
      setHasRequested(false);
      lastQueryRef.current = "";
      return undefined;
    }

    const url = buildPhotonUrl(normalized, { mode, countryCode, bbox, lang });

    if (url === lastQueryRef.current) {
      return undefined;
    }

    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    if (resilient && responseCache.has(url)) {
      if (abortRef.current) abortRef.current.abort();
      lastQueryRef.current = url;
      setResults(responseCache.get(url));
      setLoading(false);
      setHasRequested(true);
      setUnavailable(false);
      return undefined;
    }

    debounceRef.current = window.setTimeout(async () => {
      if (resilient && Date.now() < blockedUntil) {
        setResults([]);
        setLoading(false);
        setUnavailable(true);
        return;
      }

      const requestId = ++requestIdRef.current;
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      let timedOut = false;
      const timeoutId = resilient
        ? window.setTimeout(() => {
            timedOut = true;
            controller.abort();
          }, REQUEST_TIMEOUT_MS)
        : null;
      setLoading(true);
      setHasRequested(true);
      lastQueryRef.current = url;

      try {
        const res = await fetch(url, { signal: controller.signal });
        if (!res.ok) {
          const error = new Error(`Photon error: ${res.status}`);
          error.status = res.status;
          throw error;
        }
        const data = await res.json();
        const nextResults = (data.features || [])
          .map(formatFeature)
          .filter(Boolean);

        if (resilient) rememberResponse(url, nextResults);
        setResults(nextResults);
        setUnavailable(false);
      } catch (err) {
        const abortedByUs = err?.name === "AbortError" && !timedOut;
        if (abortedByUs) {
          console.log("[useCityAutocomplete] fetch aborted", { requestId });
        } else {
          setResults([]);
          // Limitation, panne ou timeout : on laisse Photon tranquille un
          // moment plutôt que de relancer à chaque frappe (risque de bannissement).
          const isOutage = timedOut || !err?.status || err.status === 429 || err.status >= 500;
          if (resilient && isOutage) {
            blockedUntil = Date.now() + COOLDOWN_MS;
            setUnavailable(true);
          }
        }
      } finally {
        if (timeoutId) window.clearTimeout(timeoutId);
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, [query, mode, countryCode, bbox, lang]);

  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, []);

  return { results, loading, hasRequested, unavailable };
};
